import { useState, type FormEvent } from "react";
import {
  dealerPermissionDefinitions,
  getDealerPermissions,
  getStaffPermissions,
  normalizeDealerPermissions,
  normalizeStaffPermissions,
  staffPermissionDefinitions,
  type DealerPermission,
  type StaffPermission,
} from "@longjie/shared";
import { ApiError, request } from "./api";
import { Button, Field, Modal } from "./components";
import type { Customer, StaffAccount } from "./types";
import { PermissionPresets } from "./PermissionPresets";
import "./account-permissions.css";

type PermissionGroup<P extends string> = { title: string; items: P[] };

const staffGroups: PermissionGroup<StaffPermission>[] = [
  { title: "经营概览", items: ["dashboard.read"] },
  { title: "订单与收款", items: ["orders.read", "orders.create", "orders.write", "orders.export", "payments.write"] },
  { title: "商品与分类", items: ["products.read", "products.write", "categories.read", "categories.write"] },
  { title: "成品仓库", items: ["inventory.read", "inventory.write"] },
  { title: "限时促销", items: ["promotions.read", "promotions.write"] },
  { title: "批量导入", items: ["imports.read", "imports.write"] },
  { title: "客户账号", items: ["customers.read", "customers.write"] },
  { title: "内部账号与日志", items: ["staff.read", "audit.read"] },
];

function PermissionPicker<P extends string>({ groups, definitions, value, onChange, normalize, disabled = false }: {
  groups: PermissionGroup<P>[];
  definitions: ReadonlyArray<{ id: P; label: string }>;
  value: P[];
  onChange: (value: P[]) => void;
  normalize: (value: P[]) => P[];
  disabled?: boolean;
}) {
  function toggle(permission: P, checked: boolean) {
    const next = checked
      ? normalize([...value, permission])
      : value.filter((candidate) => !normalize([candidate]).includes(permission));
    onChange(next);
  }
  return <div className="permission-picker form-grid__full">
    <p className="permission-picker__hint">勾选即可看到或使用。开启操作会勾选所需查看项；取消查看项，也会取消依赖它的操作。</p>
    <div className="permission-picker__tools">
      <Button type="button" variant="secondary" disabled={disabled} onClick={() => onChange(normalize(definitions.filter((item) => item.id.endsWith(".read")).map((item) => item.id)))}>全选可见内容</Button>
      <Button type="button" variant="ghost" disabled={disabled} onClick={() => onChange([])}>取消全部</Button>
      <span aria-live="polite">已勾选 {value.length} 项</span>
    </div>
    <div className={`permission-picker__groups${groups.length === 1 ? " permission-picker__groups--single" : ""}`}>
      {groups.map((group) => <fieldset key={group.title} disabled={disabled}>
        <legend>{group.title}</legend>
        {group.items.map((id) => <label key={id} className="permission-picker__item">
          <input type="checkbox" checked={value.includes(id)} onChange={(event) => toggle(id, event.target.checked)} />
          <span>{definitions.find((item) => item.id === id)?.label ?? id}</span>
        </label>)}
      </fieldset>)}
    </div>
  </div>;
}

export function StaffPermissionPicker({ value, onChange, disabled }: { value: StaffPermission[]; onChange: (value: StaffPermission[]) => void; disabled?: boolean }) {
  return <PermissionPicker groups={staffGroups} definitions={staffPermissionDefinitions} value={value} onChange={onChange} normalize={normalizeStaffPermissions} disabled={disabled ?? false} />;
}

export function DealerPermissionPicker({ value, onChange, disabled }: { value: DealerPermission[]; onChange: (value: DealerPermission[]) => void; disabled?: boolean }) {
  return <PermissionPicker groups={[{ title: "客户可见内容", items: dealerPermissionDefinitions.map((item) => item.id) }]} definitions={dealerPermissionDefinitions} value={value} onChange={onChange} normalize={normalizeDealerPermissions} disabled={disabled ?? false} />;
}

export function DealerPermissionsModal({ account, onClose, onSaved }: { account: Customer; onClose: () => void; onSaved: () => void }) {
  const [permissions, setPermissions] = useState(() => getDealerPermissions(account));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await request(`/api/v1/admin/customers/${account.id}`, { method: "PATCH", body: JSON.stringify({ dealerPermissions: permissions }) });
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "权限保存失败，请重试");
    } finally { setSaving(false); }
  }
  return <Modal title="客户账号权限" description={`${account.name} · ${account.username}。订单和地址始终只限该客户自己的记录。`} onClose={() => { if (!saving) onClose(); }} footer={<>
    <Button variant="secondary" disabled={saving} onClick={onClose}>取消</Button>
    <Button form="dealer-permissions-form" type="submit" loading={saving}>保存权限</Button>
  </>}>
    <form id="dealer-permissions-form" onSubmit={submit}>
      {error && <div className="inline-error" role="alert">{error}</div>}
      <PermissionPresets accountType="DEALER" value={permissions} onApply={setPermissions} disabled={saving} />
      <DealerPermissionPicker value={permissions} onChange={setPermissions} disabled={saving} />
      {permissions.length === 0 && <p className="readonly-note">当前未开放任何业务内容。该客户仍可登录及退出。</p>}
    </form>
  </Modal>;
}

export function StaffPermissionsModal({ account, onClose, onSaved }: { account: StaffAccount; onClose: () => void; onSaved: () => void }) {
  const [role, setRole] = useState(account.staffRole);
  const [permissions, setPermissions] = useState(() => getStaffPermissions(account));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  function changeRole(next: StaffAccount["staffRole"]) {
    setRole(next);
    if (next !== "CUSTOM") setPermissions(getStaffPermissions({ staffRole: next }));
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await request(`/api/v1/admin/staff/${account.id}`, { method: "PATCH", body: JSON.stringify({ staffRole: role, staffPermissions: permissions }) });
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "权限保存失败，请重试");
    } finally { setSaving(false); }
  }
  return <Modal title="内部账号权限" description={`${account.displayName} · ${account.username}。只有管理员能配置账号权限。`} onClose={() => { if (!saving) onClose(); }} footer={<>
    <Button variant="secondary" disabled={saving} onClick={onClose}>取消</Button>
    <Button form="staff-permissions-form" type="submit" loading={saving}>保存权限</Button>
  </>}>
    <form id="staff-permissions-form" onSubmit={submit}>
      {error && <div className="inline-error" role="alert">{error}</div>}
      <PermissionPresets accountType="STAFF" value={permissions} onApply={(value) => { setRole("CUSTOM"); setPermissions(value); }} disabled={saving} />
      <Field label="权限方式"><select value={role} disabled={saving} onChange={(event) => changeRole(event.target.value as StaffAccount["staffRole"])}>
        <option value="CUSTOM">自定义勾选</option><option value="VIEWER">查看员（全部只读）</option><option value="ADMIN">管理员（全部权限）</option>
      </select></Field>
      {role !== "CUSTOM" && <p className="readonly-note">选择“自定义勾选”后，可以逐项调整下面的内容。</p>}
      <StaffPermissionPicker value={permissions} onChange={setPermissions} disabled={saving || role !== "CUSTOM"} />
      {role === "CUSTOM" && permissions.length === 0 && <p className="readonly-note">当前未开放任何业务内容。该内部账号仍可登录及退出。</p>}
    </form>
  </Modal>;
}
